import { ActionTree, GetterTree, MutationTree } from 'vuex';
import { gql } from 'graphql-tag';
import { set } from 'lodash';
import { DateTime } from 'luxon';
import { RootState } from '~/store';

const initialState = () => ({
  id: null,
  controller: null,
  data: {
    bannerPlacement: {
      count: 0,
      results: []
    },
    selectedBanner: null
  },
  query: {
    where: {},
    sort: {
      createdAt: 'DESC'
    },
    offset: 0,
    limit: 10
  },
  table: {
    data: {
      count: 0,
      results: []
    },
    headers: [
      {
        text: 'Banner Image',
        align: 'center',
        sortable: false,
        value: 'image'
      },
      {
        text: 'Banner Name',
        align: 'center',
        value: 'name'
      },
      {
        text: 'Periode Date',
        align: 'center',
        sortable: false,
        value: 'periodeDate'
      },
      {
        text: 'Placement',
        sortable: false,
        align: 'center',
        value: 'bannerPlacement.placementName'
      },
      {
        text: 'Created Date',
        sortable: true,
        align: 'center',
        value: 'createdAt'
      },
      {
        text: '',
        align: 'end',
        sortable: false,
        value: 'actions'
      }
    ],
    footer: {
      'items-per-page-options': [10, 25, 50, 100],
      'show-current-page': true,
      'show-first-last-page': true
    },
    filter: {
      keyword: '',
      periodeDate: [],
      bannerPlacementId: []
    },
    options: {
      page: 1,
      itemsPerPage: 10,
      sortBy: ['createdAt'],
      sortDesc: [true],
      groupBy: [],
      groupDesc: [],
      multiSort: false,
      mustSort: true
    }
  },
  loading: {
    findAll: false,
    findOne: false,
    add: false,
    edit: false,
    bannerPlacementFindAll: false,
    export: false
  }
});

export const state = () => initialState();

export type PartnerMerchantBannerState = ReturnType<typeof state>;

export const getters: GetterTree<PartnerMerchantBannerState, RootState> = {
  getBannerPlacement: ({ data }) => data.bannerPlacement,
  getSelectedBanner: ({ data }) => data.selectedBanner,
  getQuery: ({ query }) => query,
  getTable: ({ table }) => table,
  getLoading: ({ loading }) => loading
};

export const mutations: MutationTree<PartnerMerchantBannerState> = {
  changeId: (state, id) => (state.id = id),
  changeData: (state, data) => (state.table.data = data),
  changeDataItem: (state, { id, data }) => {
    return (state.table.data.results = state.table.data.results.map((item) => {
      if (item.id === id) {
        return Object.assign({}, item, data);
      }
      return item;
    }));
  },
  changeSelectedBanner: (state, data) => (state.data.selectedBanner = data),
  changeQuery: (state, query) => Object.assign(state.query, query),
  changeTableItems: (state, items) => {
    Object.assign(state.table, { items });
  },
  changeTableFilter: (state, filter) => {
    Object.assign(state.table, { filter });
    Object.assign(state.query, {
      where: {
        name: filter.keyword,
        periodeDate: filter.periodeDate,
        bannerPlacementId: filter.bannerPlacementId
      }
    });
  },
  changeTableOptions: (state, options) => {
    Object.assign(state.table, { options });
    Object.assign(state.query, {
      sort: set({}, options.sortBy[0], options.sortDesc[0] ? 'DESC' : 'ASC'),
      limit: options.itemsPerPage,
      offset: (options.page - 1) * options.itemsPerPage
    });
  },
  setBannerPlacement: (state, data) => (state.data.bannerPlacement = data),
  setLoading: (state, loading: PartnerMerchantBannerState['loading']) =>
    (state.loading = Object.assign({}, state.loading, loading)),
  setController: (state, controller) => (state.controller = controller),
  resetState: (state) => Object.assign(state, initialState())
};

export const actions: ActionTree<PartnerMerchantBannerState, RootState> = {
  /**
   * Fetch merchant banner.
   */
  async findAll({ commit, state, dispatch, rootGetters }) {
    if (process.server) return;

    dispatch('controllerInit');

    const api = window.$nuxt.$options.$api;
    const emitter = window.$nuxt.$options.$emitter;

    try {
      commit('setLoading', { findAll: true });

      const variables = {
        ...state.query,
        where: {
          ...state.query.where,
          merchantId: rootGetters['partner/merchant/getId']
        }
      };
      const { data } = await api.query({
        query: gql`
          query MerchantBannerFindAll(
            $where: MerchantBannerQueryFilter
            $sort: MerchantBannerQuerySortBy
            $limit: Int
            $offset: Int
          ) {
            Merchant {
              MerchantBanner {
                findAll(
                  where: $where
                  sort: $sort
                  limit: $limit
                  offset: $offset
                ) {
                  count
                  results {
                    id
                    name
                    image
                    startDate
                    endDate
                    keyword
                    isActive
                    createdAt
                    merchantId
                    hyperlink
                    bannerPlacementId
                    bannerPlacement {
                      id
                      placementName
                    }
                  }
                }
              }
            }
          }
        `,
        variables,
        context: {
          fetchOptions: {
            signal: state.controller.signal
          }
        }
      });
      const { count, results } = data?.Merchant?.MerchantBanner?.findAll;
      const resultsMap = results.map((item) => {
        const startDate = item?.startDate
          ? DateTime.fromISO(item.startDate).toFormat('dd LLL yyyy')
          : '';
        const endDate = item?.endDate
          ? DateTime.fromISO(item.endDate).toFormat('dd LLL yyyy')
          : '';
        const periodeDate = `${startDate} - ${endDate}`;
        return {
          ...item,
          periodeDate,
          createdAt: DateTime.fromISO(item.createdAt).toFormat('dd/LL/yyyy')
        };
      });
      commit('changeData', { count, results: resultsMap });
    } catch (error) {
      if (!/signal is aborted without reason/i.test(error.toString()))
        emitter.emit('TOAST', error);
      commit('changeData', { count: 0, results: [] });
    } finally {
      commit('setLoading', { findAll: false });
    }
  },

  /**
   * Fetch merchant banner placement.
   */
  async bannerPlacementfindAll({ commit, state, dispatch }) {
    if (process.server) return;

    dispatch('controllerInit');

    const api = window.$nuxt.$options.$api;
    const emitter = window.$nuxt.$options.$emitter;

    try {
      commit('setLoading', { bannerPlacementFindAll: true });

      const variables = {
        where: {},
        sort: {},
        limit: 20,
        offset: 0
      };
      const { data } = await api.query({
        query: gql`
          query MerchantBannerPlacementFindAll(
            $where: MerchantBannerPlacementQueryFilter
            $sort: MerchantBannerPlacementQuerySortBy
            $limit: Int
            $offset: Int
          ) {
            Merchant {
              MerchantBannerPlacement {
                findAll(
                  where: $where
                  sort: $sort
                  limit: $limit
                  offset: $offset
                ) {
                  count
                  results {
                    id
                    placementName
                    description
                  }
                }
              }
            }
          }
        `,
        variables,
        context: {
          fetchOptions: {
            signal: state.controller.signal
          }
        }
      });
      const { count, results } =
        data?.Merchant?.MerchantBannerPlacement?.findAll;
      commit('setBannerPlacement', { count, results });
    } catch (error) {
      if (!/signal is aborted without reason/i.test(error.toString()))
        emitter.emit('TOAST', error);
      commit('setBannerPlacement', { count: 0, results: [] });
    } finally {
      commit('setLoading', { bannerPlacementFindAll: false });
    }
  },

  /**
   * Add banner mutation.
   */
  async addBanner(
    { commit, rootGetters },
    { form }: { form: Record<string, any> }
  ) {
    if (process.server) return;

    const api = window.$nuxt.$options.$api;

    try {
      commit('setLoading', { add: true });

      const req = {
        ...form,
        merchantId: rootGetters['partner/merchant/getId']
      };
      await api.mutate({
        mutation: gql`
          mutation MerchantBannerAdd($req: MerchantBannerForm) {
            Merchant {
              MerchantBanner {
                add(req: $req) {
                  id
                }
              }
            }
          }
        `,
        variables: { req }
      });
      commit('setLoading', { add: false });
      return { status: true, message: '' };
    } catch (error) {
      commit('setLoading', { add: false });
      return { status: false, message: error };
    }
  },

  /**
   * Edit banner mutation.
   */
  async editBanner(
    { commit },
    { id, req }: { id: string; req: Record<string, any> }
  ) {
    if (process.server) return;

    const api = window.$nuxt.$options.$api;

    try {
      commit('setLoading', { edit: true });

      await api.mutate({
        mutation: gql`
          mutation MerchantBannerEdit($id: String, $req: MerchantBannerForm) {
            Merchant {
              MerchantBanner {
                edit(id: $id, req: $req) {
                  id
                }
              }
            }
          }
        `,
        variables: { id, req }
      });
      commit('setLoading', { edit: false });
      return { status: true, message: '' };
    } catch (error) {
      commit('setLoading', { edit: false });
      return { status: false, message: error };
    }
  },

  /**
   * Creates a new AbortController object instance.
   */
  controllerInit({ commit, state }) {
    if (!state.controller) commit('setController', new AbortController());
  },

  /**
   * Controller abort for canceled request.
   */
  controllerAbort({ commit, state }) {
    if (state.controller) {
      state.controller.abort();
      commit('setController', null);
    }
  }
};
